import React from 'react';
import { Loader2 } from 'lucide-react';
import SignaturePadInline from './SignaturePadInline';

/** Purpose: Renders create-authorization modal. Inputs: modal props. Output: React render output (JSX). */
export default function CreateAuthorizationModal({
  open,
  authStep,
  setAuthStep,
  authFlowStatus,
  authFlowError,
  request,
  recordTypeLabels,
  revocationContact,
  signatureData,
  setSignatureData,
  showSignaturePad,
  setShowSignaturePad,
  isUpdating,
  onClose,
  onCreateAndAttach
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-4 rounded-t-2xl">
          <h3 className="text-lg font-semibold text-white">HIPAA Authorization</h3>
          <p className="text-green-100 text-sm">
            {authStep === 1 ? 'Review and confirm' : 'Sign to authorize'}
          </p>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {authFlowStatus === 'error' && authFlowError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {authFlowError}
            </div>
          )}

          {authStep === 1 && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-blue-900 text-sm">
                  By signing this authorization, you are allowing <strong>{request.organization}</strong> to
                  release your medical records to you as requested.
                </p>
              </div>

              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <h4 className="font-medium text-slate-900">Authorization Details</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Provider</p>
                    <p className="font-medium">{request.providerName}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Organization</p>
                    <p className="font-medium">{request.organization}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Records Requested</p>
                    <p className="font-medium">{recordTypeLabels || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Expiration</p>
                    <p className="font-medium">90 days from signing</p>
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-amber-900 text-sm font-medium mb-2">Important Notices:</p>
                <ul className="text-amber-800 text-sm space-y-1">
                  <li>• You have the right to revoke this authorization at any time</li>
                  <li>• This authorization expires in 90 days</li>
                  <li>• Your treatment will not be affected if you do not sign</li>
                </ul>
                <p className="text-xs text-amber-700 mt-2">
                  Revocation contact: {revocationContact}
                </p>
              </div>
            </div>
          )}

          {authStep === 2 && (
            <div className="space-y-4">
              <div className="text-center mb-6">
                <p className="text-slate-600">
                  By signing below, you authorize the release of your medical records.
                </p>
              </div>

              {signatureData ? (
                <div className="text-center">
                  <p className="text-sm text-slate-600 mb-2">Your Signature:</p>
                  <img
                    src={signatureData}
                    alt="Signature"
                    className="mx-auto border border-slate-300 rounded-lg max-h-24 bg-white"
                  />
                  <button
                    type="button"
                    onClick={() => setSignatureData(null)}
                    className="mt-2 text-sm text-blue-600 hover:text-blue-700"
                  >
                    Clear and re-sign
                  </button>
                </div>
              ) : showSignaturePad ? (
                <SignaturePadInline
                  onSave={(data) => {
                    setSignatureData(data);
                    setShowSignaturePad(false);
                  }}
                  onCancel={() => setShowSignaturePad(false)}
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setShowSignaturePad(true)}
                  className="w-full py-6 border-2 border-dashed border-slate-300 rounded-lg text-slate-600 hover:border-green-500 hover:text-green-600 transition-colors"
                >
                  Click to Sign
                </button>
              )}
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 px-6 py-4 flex justify-between">
          <button
            onClick={() => {
              if (authStep === 1) {
                onClose();
              } else {
                setAuthStep(1);
              }
            }}
            className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50"
          >
            {authStep === 1 ? 'Cancel' : 'Back'}
          </button>

          {authStep === 1 ? (
            <button
              onClick={() => setAuthStep(2)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700"
            >
              Continue to Sign
            </button>
          ) : (
            <button
              onClick={onCreateAndAttach}
              disabled={!signatureData || isUpdating}
              className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
            >
              {isUpdating ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Sign & Attach
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
